import SlotRequestsAdmin from "../components/SlotRequestsAdmin";
import { useEffect, useState } from "react";
import { Users, Globe, Bell, Activity, Loader2, ShieldCheck, Shield, User as UserIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { fmtDate } from "../lib/monitor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ROLE_META = {
  superadmin: { label: "Super Admin", cls: "bg-blue-100 text-blue-700", icon: ShieldCheck },
  admin: { label: "Admin", cls: "bg-violet-100 text-violet-700", icon: Shield },
  user: { label: "User", cls: "bg-slate-100 text-slate-600", icon: UserIcon },
};

const PERM_LABELS = {
  can_add_websites: "Add webapps",
  can_edit_websites: "Edit webapps",
  can_delete_websites: "Delete webapps",
};

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card className="border-slate-200 p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.1em] text-slate-500">{label}</p>
          <p className="mt-2 font-mono text-3xl font-semibold text-slate-900">{value}</p>
        </div>
        <span className="rounded-lg bg-blue-50 p-2 text-blue-600"><Icon size={18} strokeWidth={2} /></span>
      </div>
    </Card>
  );
}

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.user;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-mono font-medium ${m.cls}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

function EditUserDialog({ user, isSuper, currentUserId, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [maxSites, setMaxSites] = useState(String(user.max_websites ?? 50));
  const [perms, setPerms] = useState({ ...user.permissions });
  const [saving, setSaving] = useState(false);
  const isSelf = user.id === currentUserId;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        is_active: isActive,
        max_websites: parseInt(maxSites, 10) || 0,
        permissions: perms,
      };
      if (isSuper) payload.role = role;
      const { data } = await api.put(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit user</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Role</Label>
              <Select value={role} onValueChange={setRole} disabled={!isSuper}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
              {!isSuper && <p className="text-[11px] text-slate-400">Only a super admin can change roles.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Webapp limit</Label>
              <Input type="number" min="0" value={maxSites} onChange={(e) => setMaxSites(e.target.value)} data-testid="edit-user-maxsites" />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-slate-800">Account active</p>
              <p className="text-[11px] text-slate-400">{isSelf ? "You can't suspend yourself" : "Suspended users can't log in"}</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} disabled={isSelf} data-testid="edit-user-active" />
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-400">Permissions</p>
            <div className="divide-y divide-slate-100">
              {Object.entries(PERM_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-slate-700">{label}</span>
                  <Switch
                    checked={!!perms[key]}
                    onCheckedChange={(v) => setPerms((p) => ({ ...p, [key]: v }))}
                    data-testid={`edit-user-perm-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving && <Loader2 size={15} className="mr-2 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "superadmin";
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleting.id}`);
      toast.success(`${deleting.email} removed`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const canEdit = (u) => isSuper || u.role === "user";

  if (loading) {
    return (
      <div className="space-y-8" data-testid="admin-skeleton">
        <div className="space-y-2"><Skeleton className="h-8 w-32" /><Skeleton className="h-4 w-72" /></div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="border-slate-200 p-6"><Skeleton className="h-3 w-24" /><Skeleton className="mt-3 h-8 w-16" /></Card>
          ))}
        </div>
        <Card className="border-slate-200 p-6">
          <Skeleton className="mb-4 h-4 w-24" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 py-3.5 last:border-0">
              <Skeleton className="h-8 w-56" /><Skeleton className="h-5 w-20 rounded-full" />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-page">
      <SlotRequestsAdmin />
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Admin</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage users, roles and permissions. {isSuper ? "You are a Super Admin." : "You are an Admin."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Users" value={stats?.total_users ?? 0} icon={Users} testid="admin-stat-users" />
        <StatCard label="Total Webapps" value={stats?.total_websites ?? 0} icon={Globe} testid="admin-stat-webapps" />
        <StatCard label="Total Alerts" value={stats?.total_alerts ?? 0} icon={Bell} testid="admin-stat-alerts" />
        <StatCard label="Total Checks" value={stats?.total_checks ?? 0} icon={Activity} testid="admin-stat-checks" />
      </div>

      <div>
        <h2 className="mb-3 font-heading text-lg font-semibold tracking-tight text-slate-900">Users</h2>
        <Card className="overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {["User", "Role", "Status", "Webapps", "Joined", ""].map((h, i) => (
                    <th key={h + i} className={`px-4 py-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${i === 3 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid="admin-user-row">
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-slate-900">{u.name}{u.id === me?.id && <span className="ml-2 text-[11px] text-blue-500">(you)</span>}</div>
                      <div className="font-mono text-xs text-slate-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-3.5"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3.5">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-mono text-red-600"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Suspended</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right font-mono text-slate-700">{u.website_count} / {u.max_websites}</td>
                    <td className="px-4 py-3.5 text-slate-500">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(u)}
                          disabled={!canEdit(u)}
                          data-testid={`edit-user-${u.id}`}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          title={canEdit(u) ? "Edit" : "Only a super admin can edit this account"}
                        >
                          <Pencil size={15} />
                        </button>
                        {isSuper && (
                          <button
                            onClick={() => setDeleting(u)}
                            disabled={u.id === me?.id || u.role === "superadmin"}
                            data-testid={`delete-user-${u.id}`}
                            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            title={u.role === "superadmin" ? "Demote before deleting" : "Delete"}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {editing && (
        <EditUserDialog
          user={editing}
          isSuper={isSuper}
          currentUserId={me?.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono font-medium">{deleting?.email}</span> and all of their webapps and monitoring data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
